import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';

import Header from '../scripts/components/global/header';
import PaymentOptions from '../scripts/components/global/refund/payment-options';
import Wizard from '../scripts/components/global/wizard/wizard';
import RefundReview from '../scripts/components/global/refund/refund-review';
import RefundSuccessful from '../scripts/components/global/refund/refund-successful';
import PropTypes from 'prop-types';
import RefundFooterBottom from '../scripts/components/global/refund/refundFooterBottom';
import RefundFooter from '../scripts/components/global/refund/refundFooter';
import queryString from 'query-string';
import { RefundReasonStart, RefundReasonSuccess, retrievePnr, retrieveRefundReason, sendRefundInfoToSalesforce } from '../redux/actions';
import { getRefundReason } from '../cms-wrapper/cms';
import { aggregateResults, getUniqueSubCodes, validateCurrencyStorefront } from './refund-utils';
import ModalRefundNotApplicable from '../scripts/components/global/refund/modal-refund-not-applicable';
import TranslatedCopy from '../scripts/intl/translated-copy';
import getQuoteRefundDataActions from '../redux/actions/quoteRefund';
import getFulfillRefundDataActions from '../redux/actions/fulfillRefund';
import Loading from '../scripts/components/global/loading-component';
import RefundLoader from '../scripts/components/global/refund/refund-loader';

/**
 * Realiza llamadas individuales al CMS por cada subCode único.
 * Deduplica subCodes repetidos y maneja errores de forma aislada.
 *
 * @param { Array } quotations - Array de quotations del QuoteRefund
 * @param { string } language - Código de idioma
 * @param { Function } dispatch - Redux dispatch
 */
const fetchIndividualRefundReasons = async(quotations, language, dispatch) => {
  const uniqueSubCodes = getUniqueSubCodes(quotations);

  // Check if there are any tickets with subCode '00' (valid happy path)
  const hasTickets00 = (quotations || []).some(
    (item) => item?.quoteItemResult?.subCode?.trim() === '00'
  );

  if (uniqueSubCodes.length === 0) {
    dispatch(RefundReasonStart());
    if (hasTickets00) {
      // Happy path: all tickets are '00', no CMS call needed
      dispatch(RefundReasonSuccess([], 'quote'));
    } else {
      // Error: all subCodes are null/undefined/empty — block access
      dispatch(RefundReasonSuccess([{
        description: '',
        processing: 'E',
        reason: '',
        specialButton: 'False',
        subcode: '',
      }], 'quote'));
    }
    return;
  }

  try {
    dispatch(RefundReasonStart());
    const promises = uniqueSubCodes.map(subCode =>
      getRefundReason(subCode, language)
        .then(value => ({ status: 'fulfilled', value }))
        .catch(reason => ({ status: 'rejected', reason }))
    );
    const results = await Promise.all(promises);
    const aggregatedCodes = aggregateResults(results, uniqueSubCodes);
    dispatch(RefundReasonSuccess(aggregatedCodes, 'quote'));
  } catch (error) {
    dispatch(RefundReasonSuccess([], 'quote'));
  }
};

export const RefundView = ({
  country,
  dispatch,
  documentNumbers,
  errorFulfillRefund,
  errorMessageCms,
  errorQuoteRefund,
  fulfillRefundData,
  handleShowModal,
  isSignIn,
  language,
  location,
  loginRequired,
  openLoginModal,
  pnrInfo,
  quoteRefundData,
  refundCode96Dot5,
  refundReason,
  storefront,
  userProfile,
}) => {
  // Variables
  // Stepper Header functionality that might be added later
  // const [listOpen, setListOpen] = useState(false);
  const [radioChange, setRadioChange] = useState(false);
  const [optionSelected, setOptionSelected] = useState(false);

  const urlParams = queryString.parse(location.search);
  const params = {
    pnr: urlParams.pnr,
    lastName: urlParams['last-name'],
  };
  const destructureStorefront = (storefront || '').split('-');

  const isAwEligible = false;
  const isFopEligible = true;
  const showStepZeroOfFlow = isAwEligible && isFopEligible;
  const [accessGranted, setAccessGranted] = useState(false);
  const [currencyMismatch, setCurrencyMismatch] = useState(false);
  const [correctStorefront, setCorrectStorefront] = useState(null);

  const quoteApiHasResponded = (quoteRefundData && refundReason?.quoteMessageFetched) || errorQuoteRefund || refundReason?.errorRefundReason;
  const signInValidation = isSignIn || !loginRequired || showStepZeroOfFlow;

  // UseEffects
  useEffect(() => {
    dispatch(retrievePnr(destructureStorefront[1], destructureStorefront[0], params));
    if ((!isSignIn && loginRequired) && !showStepZeroOfFlow) {
      handleShowModal();
    }
    if (isAwEligible && !isFopEligible) {
      setOptionSelected('AW');
    } else if (!isAwEligible && isFopEligible) {
      setOptionSelected('Card');
    }
  }, []);

  useEffect(() => {
    const traveler = pnrInfo?.cart?.travelerCollection?.[0];
    const email = traveler?.email;
    const name = traveler?.displayName;
    const pnr = params?.pnr;
    const isValidName = Boolean(name) && !/undefined/.test(name);
    const hasRequiredData = Boolean(traveler) && Boolean(email) && isValidName && Boolean(pnr);

    if (hasRequiredData) {
      dispatch(getQuoteRefundDataActions(email, name, pnr));
    }
  }, [pnrInfo]);

  useEffect(() => {
    const identifier = errorMessageCms?.errorMessages?.[0]?.errorMessage?.identifier;
    if (errorMessageCms && identifier === '13330303') {
      const pnr = params?.pnr;
      dispatch(getQuoteRefundDataActions('', '', pnr));
    }
  }, [errorMessageCms]);

  useEffect(() => {
    if (isSignIn && pnrInfo?.cart?.travelerCollection) {
      const loggedUserEmail = userProfile?.passengerData?.email;
      let itemThatMatchesEmail = null;
      itemThatMatchesEmail = pnrInfo?.cart?.travelerCollection?.find((item) => {
        return item.email === loggedUserEmail;
      });
      setAccessGranted(itemThatMatchesEmail ? true : false);
    }
    if (!loginRequired) {
      setAccessGranted(true);
    }
  }, [isSignIn, pnrInfo]);

  useEffect(() => {
    if (quoteRefundData) {
      const quotationsForCMS = quoteRefundData?.quotations || [];

      const pnrCurrency = quotationsForCMS?.[0]?.quoteItemAmount?.currencyCode
        || quotationsForCMS?.[0]?.documentAmounts?.find(a => a.type === 'Total')?.currencyCode;
      if (pnrCurrency) {
        const validation = validateCurrencyStorefront(pnrCurrency, storefront);
        if (!validation.isValid) {
          setCurrencyMismatch(true);
          setCorrectStorefront(validation.correctStorefront);
          return;
        }
      }

      fetchIndividualRefundReasons(quotationsForCMS, language, dispatch);
    }
  }, [quoteRefundData]);

  useEffect(() => {
    const subcode = fulfillRefundData?.fulfillRefundResult?.subCode;
    if (!refundReason?.fulfillMessageFetched && subcode && subcode !== '' && subcode !== '00') {
      dispatch(retrieveRefundReason(subcode, language, 'fulfill'));
    }

    if (fulfillRefundData?.refunds?.length > 0) {
      const pnrEmail = pnrInfo?.cart?.travelerCollection?.[0]?.email;
      dispatch(sendRefundInfoToSalesforce({ fulfillRefundData, quoteRefundData, pnrEmail }));
    }
  }, [fulfillRefundData]);

  // Methods
  // Stepper Header functionality that might be added later
  // const toggleList = () => {
  //   setListOpen(!listOpen);
  // };

  const toggleRadioBtnChange = (value) => {
    setRadioChange(value);
  };

  const handleOptionSelected = (val) => {
    setOptionSelected(val);
  };

  const handleRetry = () => {
    if (errorQuoteRefund) {
      const email = pnrInfo?.cart?.travelerCollection?.[0]?.email;
      const name = pnrInfo?.cart?.travelerCollection?.[0]?.displayName;
      const pnr = params?.pnr;
      dispatch(getQuoteRefundDataActions(email, name, pnr));
    }
    if (refundReason?.errorRefundReason && quoteRefundData) {
      fetchIndividualRefundReasons(quoteRefundData?.quotations || [], language, dispatch);
    }
    if (errorFulfillRefund) {
      const email = pnrInfo?.cart?.travelerCollection?.[0]?.email;
      const name = pnrInfo?.cart?.travelerCollection?.[0]?.displayName;
      const pnr = params?.pnr || pnrInfo?.pnrCode;
      dispatch(getFulfillRefundDataActions(email, name, pnr, documentNumbers, refundCode96Dot5));
    }
  };

  const confirmRefund = () => {
    const email = pnrInfo?.cart?.travelerCollection?.[0]?.email;
    const name = pnrInfo?.cart?.travelerCollection?.[0]?.displayName;
    const pnr = params?.pnr || pnrInfo?.pnrCode;
    dispatch(getFulfillRefundDataActions(email, name, pnr, documentNumbers, refundCode96Dot5));
  };

  const specialRefund = (storefront, reason) => {
    const firstName = pnrInfo?.cart?.travelerCollection?.[0]?.firstName || '';
    const middleName = pnrInfo?.cart?.travelerCollection?.[0]?.middleName || '';

    // Tickets desde travelerCollection
    const travelerTickets = [];
    (pnrInfo?.cart?.travelerCollection || []).forEach(traveler => {
      (traveler?.allTicketNumbers || []).forEach(ticket => {
        travelerTickets.push(ticket);
      });
    });

    // Tickets desde quoteRefundData.quotations (incluye infantes)
    const quotationTickets = (quoteRefundData?.quotations || [])
      .map(quotation => quotation?.documentNumber)
      .filter(Boolean);

    // Merge sin duplicados
    const allTickets = [...new Set([...travelerTickets, ...quotationTickets])];

    const infoRefund = {
      reason: reason,
      passenger: {
        name: `${firstName}${middleName ? ` ${middleName}` : ''}`,
        lastName: pnrInfo?.cart?.travelerCollection?.[0]?.lastName || '',
      },
      CuentaPremier: '', // Número de socio AM Rewards
      email: pnrInfo?.cart?.travelerCollection?.[0]?.email || '', // Correo electrónico
      documentNumber: allTickets, // Tickets number (merge travelerCollection + quotations)
      recordLocator: pnrInfo?.pnrCode,
      description: '',  // pendiente *****
      Fop1: '', // Terminación de forma de pago
      Fop2: '', // Terminación de forma de pago
      quoteItemAmount: {
        currency: '', // Moneda - quoteItemAmount.currency
        reinstallPoints: '', // clubPremier.miles
        boletoPremiaType: '', // clubPremier.boletoPremiaType
        amount: '', // quoteItemAmount.value
      },
    };
    const infoRefundEncoded = btoa(JSON.stringify(infoRefund));
    const env = window.location.hostname;
    const isProd = /^((www|beta|alpha).)*(aeromexico)\.com$/gi.test(env);
    const linkSpecialRefunds = isProd ? 'https://cxsv.my.site.com/s/amx-atc' : 'https://cxsv--qa.sandbox.my.site.com/AMCustomerService/s/amx-atc';
    window.location.replace(`${linkSpecialRefunds}?language=${storefront}&infoRefund=${infoRefundEncoded}`);
  };

  return (
    <div className='RefundView'>
      <Header
        country={ country }
        language={ language }
        refunds={ true }
        showInformation={ false }
      />
      { currencyMismatch &&
        <div className='ModalRefundNotApplicable'>
          <div className='ModalRefundNotApplicable-detailsContainer'>
            <div className='ModalRefundNotApplicable-detailsContainer-warning'>
              <TranslatedCopy
                className='ModalRefundNotApplicable-detailsContainer-warning--alertText'
                copyID='MYB_TICKETS_NOT_ELIGIBLE_FOR_REFUND'
                tagName='p'
              />
              <TranslatedCopy
                className='ModalRefundNotApplicable-detailsContainer-warning--alertDesc'
                copyID='MYB_CURRENCY_MISMATCH'
                tagName='p'
              />
            </div>
            { correctStorefront &&
              <button
                className='ModalRefundNotApplicable-detailsContainer--btn'
                onClick={ () => {
                  const env = window.location.hostname;
                  const isProd = /^((www|beta|alpha).)*(aeromexico)\.com$/gi.test(env);
                  const baseUrl = isProd ? 'https://www.aeromexico.com' : `http://${ window.location.host }`;
                  window.location.replace(`${ baseUrl }/myb/${ correctStorefront }/reembolsos?pnr=${ params?.pnr }&last-name=${ params?.lastName }`);
                } }
              >
                <TranslatedCopy copyID='MYB_GO_TO_CORRECT_STOREFRONT' />
              </button>
            }
          </div>
        </div>
      }
      { !currencyMismatch && quoteApiHasResponded && signInValidation ?
        <Wizard
          accessGranted={ accessGranted }
          confirmRefund={ confirmRefund }
          dispatch={ dispatch }
          header={ <></> }
          footer={
            <RefundFooter
              dispatch={ dispatch }
              handleShowModal={ handleShowModal }
              isAwEligible={ isAwEligible }
              isFopEligible={ isFopEligible }
              openLoginModal={ openLoginModal }
              optionSelected={ optionSelected }
              params={ params }
              radioChange={ radioChange }
              specialRefund={ specialRefund }
            />
          }
          isSignIn={ isSignIn }
          refundLoader={ <RefundLoader /> }
          refundNotEligible={
            <ModalRefundNotApplicable
              accessGranted={ accessGranted }
              dispatch={ dispatch }
              fulfillRefundData={ fulfillRefundData }
              handleRetry={ handleRetry }
              specialRefund={ specialRefund }
            />
          }
          specialRefund={ specialRefund }
          showStepZeroOfFlow={ showStepZeroOfFlow }
        >
          <PaymentOptions
            toggleRadioBtnChange={ toggleRadioBtnChange }
            handleOptionSelected={ handleOptionSelected }
          />
          <RefundReview
            dispatch={ dispatch }
            optionSelected={ optionSelected }
          />
          <RefundSuccessful
            optionSelected={ optionSelected }
            specialRefund={ specialRefund }
          />
        </Wizard>
        : <Loading
          refunds={ true }
        />
      }
      <RefundFooterBottom
        country={ country }
        language={ language } />
    </div>
  );
};

RefundView.propTypes = {
  country: PropTypes.string,
  dispatch: PropTypes.func,
  documentNumbers: PropTypes.array,
  errorFulfillRefund: PropTypes.bool,
  fulfillRefundData: PropTypes.object,
  handleShowModal: PropTypes.func,
  isSignIn: PropTypes.bool,
  language: PropTypes.string,
  location: PropTypes.object,
  openLoginModal: PropTypes.func,
  pnrInfo: PropTypes.object,
  quoteRefundData: PropTypes.object,
  refundCode96Dot5: PropTypes.bool,
  storefront: PropTypes.string,
  userProfile: PropTypes.object,
};

const mapStateToProps = (state) => ({
  country: state.LanguageBundle.country,
  documentNumbers: state.refundPassengerList.documentNumbers,
  errorFulfillRefund: state.fulfillRefund.errorFulfillRefund,
  errorMessageCms: state.ErrorMessage?.errorMessageCms,
  errorQuoteRefund: state.quoteRefund.errorQuoteRefund,
  fulfillRefundData: state.fulfillRefund.fulfillRefundData,
  language: state.Manage.language,
  loginRequired: state.Manage.stateStoreFront.loginRequired,
  pnrInfo: state.Manage.pnrInfo,
  quoteRefundData: state?.quoteRefund?.quoteRefundData,
  refundCode96Dot5: state.refundCode96Dot5.refundCode96Dot5,
  refundReason: state.refundReason,
  storefront: state.Manage.storefront,
  userProfile: state.UserProfileSession.userProfile,
});

export default connect(mapStateToProps)(RefundView);
